/**
 * Book Appointment Page - Create new appointment
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAppointment } from '@/contexts/AppointmentContext';
import { useBranch } from '@/contexts/BranchContext';
import { useBarber } from '@/contexts/BarberContext';
import { useService } from '@/contexts/ServiceContext';
import { Button } from '@/components/ui/Button';
import { ArrowLeft, Sunrise, Sunset } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Timestamp } from 'firebase/firestore';

export default function BookAppointmentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signInAsGuest } = useAuth();
  const { createAppointment, getAvailableTimeSlots } = useAppointment();
  const { branches, listBranches } = useBranch();
  const { barbers, listBarbers } = useBarber();
  const { services, listServices } = useService();

  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);

  const [formData, setFormData] = useState({
    branchId: '',
    barberId: '',
    serviceId: '',
    date: '',
    time: '',
    notes: '',
  });

  useEffect(() => {
    // Filter branches by user's franchiseId if they have one
    if (user?.franchiseId) {
      listBranches({ franchiseId: user.franchiseId });
    } else {
      // If no franchiseId, show all active branches (fallback)
      listBranches({ isActive: true });
    }
    listServices();
  }, [user?.franchiseId]);

  useEffect(() => {
    if (formData.branchId) {
      listBarbers(formData.branchId);
    }
  }, [formData.branchId]);

  useEffect(() => {
    if (formData.barberId && formData.date) {
      loadAvailableSlots();
    }
  }, [formData.barberId, formData.date]);

  const loadAvailableSlots = async () => {
    if (!formData.barberId || !formData.date) return;

    setLoadingSlots(true);
    try {
      const date = new Date(formData.date);
      const slots = await getAvailableTimeSlots(formData.barberId, date);
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error loading available slots:', error);
      toast.error('Error al cargar horarios disponibles');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.branchId || !formData.barberId || !formData.serviceId || !formData.date || !formData.time) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      const selectedBarber = barbers?.find(b => b.barberId === formData.barberId);
      const selectedService = services?.find(s => s.serviceId === formData.serviceId);
      const selectedBranch = branches?.find(b => b.branchId === formData.branchId);

      if (!selectedBarber || !selectedService || !selectedBranch) {
        toast.error('Error: datos seleccionados no válidos');
        return;
      }

      // Combine date and time
      const [hours, minutes] = formData.time.split(':');
      const appointmentDate = new Date(formData.date);
      appointmentDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      // Build appointment data, only include notes if provided
      const appointmentData: any = {
        franchiseId: selectedBranch.franchiseId,
        branchId: formData.branchId,
        barberId: formData.barberId,
        barberName: selectedBarber.displayName,
        serviceId: formData.serviceId,
        serviceName: selectedService.name,
        servicePrice: selectedService.price,
        serviceDuration: selectedService.duration,
        dateTime: Timestamp.fromDate(appointmentDate),
      };

      // Only add notes if it has a value
      if (formData.notes && formData.notes.trim()) {
        appointmentData.notes = formData.notes.trim();
      }

      const appointmentId = await createAppointment(appointmentData);

      toast.success('Cita agendada exitosamente');
      navigate(`/appointments/${appointmentId}`);
    } catch (error: any) {
      console.error('Error booking appointment:', error);
      toast.error(error.message || 'Error al agendar la cita');
    } finally {
      setLoading(false);
    }
  };

  const selectedService = services?.find(s => s.serviceId === formData.serviceId);

  // Group available slots into morning (< 14:00) and afternoon (>= 14:00) for easier scanning
  const morningSlots = availableSlots.filter(slot => parseInt(slot.split(':')[0], 10) < 14);
  const afternoonSlots = availableSlots.filter(slot => parseInt(slot.split(':')[0], 10) >= 14);

  const renderSlotButton = (slot: string) => (
    <button
      key={slot}
      type="button"
      onClick={() => setFormData(prev => ({ ...prev, time: slot }))}
      className={`min-h-[48px] px-3 py-2 rounded-lg text-base font-medium transition-colors ${
        formData.time === slot
          ? 'bg-info text-white'
          : 'bg-surface-2 text-ink-muted hover:bg-surface-2 text-ink-muted hover:bg-surface-3'
      }`}
    >
      {slot}
    </button>
  );

  // Auth check - require login or guest
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="text-center max-w-md px-6">
          <h2 className="text-2xl font-bold text-ink mb-4">Inicia sesión</h2>
          <p className="text-ink-muted mb-6">Debes iniciar sesión para agendar una cita</p>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-info text-white px-6 py-3 rounded-lg hover:bg-info/90 transition-colors font-semibold"
            >
              Iniciar Sesión
            </button>

            <button
              onClick={async () => {
                try {
                  await signInAsGuest();
                } catch (error) {
                  console.error('Error signing in as guest:', error);
                  toast.error('Error al iniciar sesión como invitado');
                }
              }}
              className="w-full bg-surface-2 text-ink px-6 py-3 rounded-lg hover:bg-surface-3 transition-colors font-semibold"
            >
              Continuar como Invitado
            </button>
          </div>

          <p className="text-base text-ink-muted mt-4">
            Como invitado podrás agendar citas sin crear una cuenta. Podrás vincular tu cuenta después si lo deseas.
          </p>
        </div>
      </div>
    );
  }

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  // Get maximum date (30 days from now)
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 30);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/appointments')}
          className="flex items-center gap-2 text-ink-muted hover:text-ink dark:hover:text-white mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver a citas
        </button>
        <h1 className="text-3xl font-bold text-ink">Agendar Cita</h1>
        <p className="mt-2 text-ink-muted">
          Selecciona la sucursal, barbero, servicio y horario
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-surface rounded-lg shadow-sm p-6 space-y-6">
        {/* Branch Selection */}
        <div>
          <label htmlFor="branchId" className="block text-base font-medium text-ink-muted mb-2">
            Sucursal *
          </label>
          <select
            id="branchId"
            name="branchId"
            aria-label="Sucursal"
            value={formData.branchId}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface-3 text-ink"
          >
            <option value="">Selecciona una sucursal</option>
            {branches?.map(branch => (
              <option key={branch.branchId} value={branch.branchId}>
                {branch.name} - {branch.city}
              </option>
            ))}
          </select>
        </div>

        {/* Barber Selection */}
        <div>
          <label htmlFor="barberId" className="block text-base font-medium text-ink-muted mb-2">
            Barbero *
          </label>
          <select
            id="barberId"
            name="barberId"
            aria-label="Barbero"
            value={formData.barberId}
            onChange={handleChange}
            required
            disabled={!formData.branchId}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface-3 text-ink disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Selecciona un barbero</option>
            {/* For appointments, show all active barbers (isAvailable is for real-time queue, not scheduled appointments) */}
            {barbers?.filter(b => b.isActive).map(barber => (
              <option key={barber.barberId} value={barber.barberId}>
                {barber.displayName}
              </option>
            ))}
          </select>
        </div>

        {/* Service Selection */}
        <div>
          <label htmlFor="serviceId" className="block text-base font-medium text-ink-muted mb-2">
            Servicio *
          </label>
          <select
            id="serviceId"
            name="serviceId"
            aria-label="Servicio"
            value={formData.serviceId}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface-3 text-ink"
          >
            <option value="">Selecciona un servicio</option>
            {services?.filter(s => s.isActive).map(service => (
              <option key={service.serviceId} value={service.serviceId}>
                {service.name} - €{(service.price / 100).toFixed(2)} ({service.duration} min)
              </option>
            ))}
          </select>
        </div>

        {/* Date Selection */}
        <div>
          <label htmlFor="date" className="block text-base font-medium text-ink-muted mb-2">
            Fecha *
          </label>
          <input
            type="date"
            id="date"
            name="date"
            aria-label="Fecha"
            value={formData.date}
            onChange={handleChange}
            min={today}
            max={maxDateStr}
            required
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface-3 text-ink"
          />
        </div>

        {/* Time Selection */}
        <div>
          <label htmlFor="time" className="block text-base font-medium text-ink-muted mb-2">
            Hora *
          </label>

          {loadingSlots ? (
            <div className="text-center py-4">
              <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-info/30 border-r-transparent"></div>
              <p className="mt-2 text-base text-ink-muted">Cargando horarios...</p>
            </div>
          ) : formData.barberId && formData.date ? (
            availableSlots.length > 0 ? (
              <div className="space-y-4">
                {morningSlots.length > 0 && (
                  <div>
                    <h3 className="flex items-center gap-2 text-base font-semibold text-ink mb-2">
                      <Sunrise className="w-5 h-5" aria-hidden="true" />
                      {t('appointments.timeSlots.morning')}
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {morningSlots.map(renderSlotButton)}
                    </div>
                  </div>
                )}
                {afternoonSlots.length > 0 && (
                  <div>
                    <h3 className="flex items-center gap-2 text-base font-semibold text-ink mb-2">
                      <Sunset className="w-5 h-5" aria-hidden="true" />
                      {t('appointments.timeSlots.afternoon')}
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {afternoonSlots.map(renderSlotButton)}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-base text-ink-muted py-4">
                No hay horarios disponibles para esta fecha. Por favor selecciona otra fecha.
              </p>
            )
          ) : (
            <p className="text-base text-ink-muted py-4">
              Selecciona un barbero y una fecha para ver los horarios disponibles
            </p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-base font-medium text-ink-muted mb-2">
            Notas (opcional)
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface-3 text-ink"
            placeholder="Alguna indicación especial..."
          />
        </div>

        {/* Price Summary */}
        {selectedService && (
          <div className="bg-info/10 border border-info/30 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-ink">Total a pagar</p>
                <p className="text-sm text-ink-muted mt-1">
                  {selectedService.name} ({selectedService.duration} minutos)
                </p>
              </div>
              <p className="text-2xl font-bold text-info">
                €{(selectedService.price / 100).toFixed(2)}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/appointments')}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading || !formData.time}
            className="flex-1"
          >
            {loading ? 'Agendando...' : 'Agendar Cita'}
          </Button>
        </div>
      </form>
    </div>
  );
}
